import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/data';

const CATEGORY_SEO: Record<string, { title: string; description: string; keywords: string }> = {
  dianying: {
    title: '电影专区 - 高清正版电影在线观看 | 91天堂',
    description: '91天堂电影专区提供海量正版高清电影资源，涵盖动作、喜剧、科幻、剧情等多种类型。每日更新最新电影，4K超清画质，极速加载，安全稳定。',
    keywords: '电影,高清电影,正版电影,在线电影,最新电影,4K电影',
  },
  dianshiju: {
    title: '电视剧专区 - 热门电视剧在线观看 | 91天堂',
    description: '91天堂电视剧专区汇集热门国产剧、海外剧、网剧等海量正版电视剧资源。每日同步更新最新剧集，4K超清画质，畅享追剧体验。',
    keywords: '电视剧,国产剧,海外剧,热门剧集,在线追剧,高清电视剧',
  },
  zongyi: {
    title: '综艺专区 - 热门综艺节目在线观看 | 91天堂',
    description: '91天堂综艺专区提供搞笑、音乐、竞技、真人秀等热门综艺节目高清资源。每日更新最新综艺内容，畅享欢笑与感动。',
    keywords: '综艺,综艺节目,真人秀,搞笑综艺,音乐综艺,竞技综艺',
  },
  dongman: {
    title: '动漫专区 - 正版动漫在线观看 | 91天堂',
    description: '91天堂动漫专区覆盖日本动漫、国产动画、欧美动画全集。经典番剧与新番同步放送，中文字幕高清画质，动漫迷的专属频道。',
    keywords: '动漫,日本动漫,国产动画,新番,经典番剧,在线动漫',
  },
  jilu: {
    title: '纪录片专区 - 顶级纪录片在线观看 | 91天堂',
    description: '91天堂纪录片专区收录BBC、Discovery、NHK等顶级纪录片资源。探索自然、历史、科技与人文，高清画质震撼视听体验。',
    keywords: '纪录片,BBC纪录片,自然纪录片,历史纪录片,科技纪录片,人文纪录片',
  },
  tiyu: {
    title: '体育专区 - 精彩体育赛事回放 | 91天堂',
    description: '91天堂体育专区提供足球、篮球、网球等热门体育赛事高清回放。多机位视角，专业解说，让您不错过每一个精彩瞬间。',
    keywords: '体育,足球,篮球,网球,赛事回放,体育直播',
  },
  yinyue: {
    title: '音乐专区 - MV演唱会在线观看 | 91天堂',
    description: '91天堂音乐专区收录MV、演唱会、音乐节目高清资源。流行、古典、摇滚等多元风格，震撼音效带来极致音乐体验。',
    keywords: '音乐,MV,演唱会,音乐节目,流行音乐,摇滚音乐',
  },
  youxi: {
    title: '游戏专区 - 游戏攻略实况解说 | 91天堂',
    description: '91天堂游戏专区提供热门游戏攻略、实况解说、电竞赛事回放。单机大作、网络游戏、手游全覆盖，游戏玩家的专属频道。',
    keywords: '游戏,游戏攻略,实况解说,电竞赛事,单机游戏,网络游戏',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6;

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const seo = CATEGORY_SEO[activeCategory] || CATEGORY_SEO.dianying;

  const filteredArticles = useMemo(() => {
    return articles.filter((a) => a.categoryId === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const pagedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setCurrentPage(1);
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: currentCategory?.name || '分类' },
              ]}
            />
            <div className="pb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {currentCategory?.name}专区
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200 sticky top-16 z-30">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {pagedArticles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {pagedArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group rounded-xl bg-background-100 border border-background-200 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full h-48 overflow-hidden">
                        <img
                          src={article.coverImage}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="px-2.5 py-1 rounded-full bg-background-900/70 text-background-50 text-xs font-medium backdrop-blur-sm">
                            {currentCategory?.name}
                          </span>
                        </div>
                      </div>
                      <div className="p-4">
                        <h2 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {article.title}
                        </h2>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {article.viewCount.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-200 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: seo.title,
            description: seo.description,
            url: typeof window !== 'undefined' ? window.location.href : '',
            about: {
              '@type': 'Thing',
              name: currentCategory?.name,
              description: currentCategory?.description,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: typeof window !== 'undefined' ? window.location.origin + '/' : '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: currentCategory?.name,
              },
            ],
          }),
        }}
      />
    </>
  );
}